/**
 * Accept Invite Page - Accept a share invitation
 *
 * Handles the /invite/:code route to accept monitoring invitations.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  relationshipService,
  type InviteValidation,
} from "../services/relationships";
import { authService, type UserRole } from "../services/auth";
import { Router, type RouterLocation } from "@vaadin/router";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/card/card.js";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import "@awesome.me/webawesome/dist/components/badge/badge.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/radio/radio.js";
import "@awesome.me/webawesome/dist/components/radio-group/radio-group.js";

@customElement("accept-invite-page")
export class AcceptInvitePage extends LitElement {
  @state() private code = "";
  @state() private isLoading = true;
  @state() private isAccepting = false;
  @state() private validation: InviteValidation | null = null;
  @state() private error = "";
  @state() private success = false;
  @state() private chosenRole: "monitor" | "clinician" = "monitor";

  static styles = css`
    :host {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      padding: 1rem;
      background: #f0f4f8;
    }

    .card {
      max-width: 450px;
      width: 100%;
    }

    .card-content {
      padding: 1.5rem;
    }

    h1 {
      margin: 0 0 1rem 0;
      font-size: 1.25rem;
      text-align: center;
    }

    .description {
      color: #6c757d;
      text-align: center;
      margin-bottom: 1.5rem;
    }

    .info-box {
      background: #f8f9fa;
      border-radius: 0.5rem;
      padding: 1rem;
      margin-bottom: 1.5rem;
    }

    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.5rem 0;
    }

    .info-row:not(:last-child) {
      border-bottom: 1px solid #e9ecef;
    }

    .info-label {
      color: #6c757d;
      font-size: 0.875rem;
    }

    .info-value {
      font-weight: 500;
    }

    .roles {
      display: flex;
      gap: 0.25rem;
    }

    .actions {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .spinner-container {
      display: flex;
      justify-content: center;
      padding: 2rem;
    }

    .error-container,
    .success-container {
      text-align: center;
    }

    .success-icon {
      font-size: 3rem;
      color: #28a745;
      margin-bottom: 1rem;
    }

    .error-icon {
      font-size: 3rem;
      color: #dc3545;
      margin-bottom: 1rem;
    }

    .login-prompt {
      background: #fff3cd;
      border-radius: 0.5rem;
      padding: 1rem;
      margin-bottom: 1rem;
      text-align: center;
    }
  `;

  // Router lifecycle hook to get the invite code
  onAfterEnter(location: RouterLocation) {
    this.code = location.params.code as string;
    this.validateInvitation();
  }

  private async validateInvitation() {
    this.isLoading = true;
    this.error = "";

    try {
      this.validation = await relationshipService.validateInvitation(this.code);
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Invalid invitation";
    } finally {
      this.isLoading = false;
    }
  }

  render() {
    return html`
      <wa-card class="card">
        <div class="card-content">
          ${this.isLoading
            ? this.renderLoading()
            : this.success
              ? this.renderSuccess()
              : this.error && !this.validation
                ? this.renderError()
                : this.renderInvitation()}
        </div>
      </wa-card>
    `;
  }

  private renderLoading() {
    return html`
      <div class="spinner-container">
        <wa-spinner style="font-size: 2rem;"></wa-spinner>
      </div>
    `;
  }

  private renderError() {
    return html`
      <div class="error-container">
        <wa-icon class="error-icon" name="circle-exclamation"></wa-icon>
        <h1>Invalid Invitation</h1>
        <p class="description">${this.error}</p>
        <wa-button variant="brand" @click=${() => Router.go("/")}>
          Go to Home
        </wa-button>
      </div>
    `;
  }

  private renderSuccess() {
    return html`
      <div class="success-container">
        <wa-icon class="success-icon" name="circle-check"></wa-icon>
        <h1>You're Connected!</h1>
        <p class="description">
          You can now view ${this.validation?.diabeticDisplayName}'s glucose
          data.
        </p>
        <wa-button variant="brand" @click=${() => Router.go("/monitoring")}>
          View Dashboard
        </wa-button>
      </div>
    `;
  }

  private renderInvitation() {
    const authState = authService.state;
    const isLoggedIn = authState.isAuthenticated;

    // Check if user has an allowed role
    const userRoles = authState.roles || [];
    const allowedRoles = this.validation?.allowedRoles || [];
    const matchingRoles = allowedRoles.filter((role) =>
      userRoles.includes(role as UserRole),
    );
    const hasAllowedRole = matchingRoles.length > 0;
    const needsRole = isLoggedIn && !hasAllowedRole;

    // Auto-select first matching role
    if (hasAllowedRole && !matchingRoles.includes(this.chosenRole)) {
      this.chosenRole = matchingRoles[0] as "monitor" | "clinician";
    }

    return html`
      <h1>
        <wa-icon name="share-nodes" style="vertical-align: middle;"></wa-icon>
        Data Sharing Invitation
      </h1>

      <p class="description">
        ${this.validation?.diabeticDisplayName} has invited you to monitor their
        glucose data.
      </p>

      <div class="info-box">
        <div class="info-row">
          <span class="info-label">From</span>
          <span class="info-value"
            >${this.validation?.diabeticDisplayName}</span
          >
        </div>
        <div class="info-row">
          <span class="info-label">Accepted as</span>
          <div class="roles">
            ${this.validation?.allowedRoles.map(
              (role) =>
                html`<wa-badge
                  variant="${role === "monitor" ? "brand" : "neutral"}"
                >
                  ${role === "monitor"
                    ? "Friend/Family"
                    : "Healthcare Professional"}
                </wa-badge>`,
            )}
          </div>
        </div>
      </div>

      ${this.error
        ? html`<wa-callout variant="danger" open style="margin-bottom: 1rem;">
            ${this.error}
          </wa-callout>`
        : ""}
      ${!isLoggedIn
        ? html`
            <div class="login-prompt">
              <wa-icon name="circle-info"></wa-icon>
              You need to log in or create an account to accept this invitation.
            </div>
            <div class="actions">
              <wa-button variant="brand" @click=${() => this.redirectToLogin()}>
                Log In / Sign Up
              </wa-button>
              <wa-button variant="text" @click=${() => Router.go("/")}>
                Cancel
              </wa-button>
            </div>
          `
        : needsRole
          ? html`
              <wa-callout variant="warning" open style="margin-bottom: 1rem;">
                Your account doesn't have the required role
                (${allowedRoles.join(" or ")}) to accept this invitation. Please
                contact ${this.validation?.diabeticDisplayName} for a new
                invitation.
              </wa-callout>
              <wa-button @click=${() => Router.go("/")}> Go to Home </wa-button>
            `
          : html`
              ${matchingRoles.length > 1
                ? html`
                    <div class="role-selection" style="margin-bottom: 1rem;">
                      <label
                        style="display: block; margin-bottom: 0.5rem; font-weight: 500;"
                        >Accept as:</label
                      >
                      ${matchingRoles.map(
                        (role) => html`
                          <wa-radio
                            name="role"
                            value=${role}
                            ?checked=${this.chosenRole === role}
                            @click=${() =>
                              (this.chosenRole = role as
                                | "monitor"
                                | "clinician")}
                          >
                            ${role === "monitor"
                              ? "Friend/Family"
                              : "Healthcare Professional"}
                          </wa-radio>
                        `,
                      )}
                    </div>
                  `
                : ""}
              <div class="actions">
                <wa-button
                  variant="brand"
                  ?loading=${this.isAccepting}
                  @click=${this.acceptInvitation}
                >
                  <wa-icon slot="prefix" name="circle-check"></wa-icon>
                  Accept Invitation
                </wa-button>
                <wa-button variant="text" @click=${() => Router.go("/")}>
                  Decline
                </wa-button>
              </div>
            `}
    `;
  }

  private redirectToLogin() {
    // Store the invite code to return after login
    sessionStorage.setItem("pendingInvite", this.code);
    Router.go("/login");
  }

  private async acceptInvitation() {
    this.isAccepting = true;
    this.error = "";

    try {
      await relationshipService.acceptInvitation(this.code, this.chosenRole);
      this.success = true;
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to accept invitation";
    } finally {
      this.isAccepting = false;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "accept-invite-page": AcceptInvitePage;
  }
}
